import React from "react";

import _ from "lodash";

import { getProgramInternalName } from "utils";

import { COUNTRY_CODE, CURRENCY_CODE } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  BEEF_METHODOLOGY,
  CROPS_METHODOLOGY,
  CarbonProgram,
  DAIRY_METHODOLOGY,
  ENROLLMENT_TYPE,
  PORK_METHODOLOGY,
  Partner,
  Practice,
} from "models";

import { AdminSection, Button, Col, Flex, Form, FormInstance, Input, InputNumber, Option, Row, Select, Switch, TextArea, Typography } from "components";

import { DraftFormValues } from "pages/admin/carbonPrograms/drafts/draftConfig";

// Countries the platform runs programs in (a subset of COUNTRY_CODE, which also holds language codes)
const PROGRAM_COUNTRIES = [
  COUNTRY_CODE.GERMANY,
  COUNTRY_CODE.AUSTRIA,
  COUNTRY_CODE.SWITZERLAND,
  COUNTRY_CODE.POLAND,
  COUNTRY_CODE.UNITED_KINGDOM,
  COUNTRY_CODE.CZECHIA,
  COUNTRY_CODE.SLOVAKIA,
  COUNTRY_CODE.USA,
  COUNTRY_CODE.HUNGARY,
];

// mirrors the API schema: a real month and day
const MONTH_DAY_RULE = { pattern: /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])$/, message: "Use MM/DD, e.g. 07/31" };
const SLUG_RULE = { pattern: /^\S+$/, message: "No spaces, e.g. zott-pl" };
// mirrors the API's versionStringSchema
const VERSION_RULE = { pattern: /^(?:latest|draft|\d{4}-\d{2}-\d{2}-kefis)$/, message: "Use latest, draft or a dated version like 2026-09-30-kefis" };

const path = (dotPath: string) => dotPath.split(".");

interface DraftFormProps {
  form: FormInstance<DraftFormValues>;
  initialValues: DraftFormValues;
  initialNotes: string | null;
  disabled: boolean;
  currentPractices: Practice[];
  livePrograms: CarbonProgram[];
  partners: Partner[];
  crops: { id: string; name: string }[];
}

export const DraftForm = ({ form, initialValues, initialNotes, disabled, currentPractices, livePrograms, partners, crops }: DraftFormProps) => {
  const accountingMethodology = Form.useWatch("accountingMethodology", form) ?? initialValues.accountingMethodology;
  const isInventory = accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;
  const initial = (dotPath: string) => _.get(initialValues, dotPath);
  const initialText = (dotPath: string) => initial(dotPath) as string | undefined;
  const initialNumber = (dotPath: string) => initial(dotPath) as number | null | undefined;

  const switchField = (dotPath: string, label: string) => (
    <Flex gap={8} align="center" key={dotPath}>
      <Form.Item name={path(dotPath)} valuePropName="checked" initialValue={initial(dotPath)} noStyle>
        <Switch size="small" />
      </Form.Item>
      <Typography variant="paragraph-m">{label}</Typography>
    </Flex>
  );

  const cropSelect = (dotPath: string, label: string) => (
    <Select name={path(dotPath)} label={label} mode="multiple" maxTagCount="responsive" showSearch allowClear initialValue={initial(dotPath)}>
      {crops.map((crop) => (
        <Option key={crop.id} value={crop.id}>
          {crop.name}
        </Option>
      ))}
    </Select>
  );

  const methodologySelect = (dotPath: string, label: string, methodologies: Record<string, string>) => (
    <Select name={path(dotPath)} label={label} allowClear initialValue={initial(dotPath)}>
      {Object.values(methodologies).map((methodology) => (
        <Option key={methodology} value={methodology}>
          {methodology}
        </Option>
      ))}
    </Select>
  );

  return (
    <Form className="midi-form" form={form} disabled={disabled} name="carbon-program-draft">
      <AdminSection headline="Program" />
      <Row gutter={16}>
        <Col sm={24} md={12}>
          <Input name="programName" label="Name farmers see" rules={[{ required: true }]} initialValue={initialText("programName")} />
        </Col>
        <Col sm={24} md={12}>
          <Input name="internalName" label="Internal name (admin only)" initialValue={initialText("internalName")} />
        </Col>
        <Col sm={24} md={12}>
          <Input name="programSlug" label="Slug (feature flag key)" rules={[{ required: true }, SLUG_RULE]} initialValue={initialText("programSlug")} />
        </Col>
        <Col sm={24} md={12}>
          <Select name="partnerId" label="Partner (none means offsetting)" showSearch allowClear initialValue={initial("partnerId")}>
            {partners.map((partner) => (
              <Option key={partner.id} value={partner.id}>
                {partner.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col sm={24} md={8}>
          <Select name="accountingMethodology" label="Accounting methodology" rules={[{ required: true }]} initialValue={initial("accountingMethodology")}>
            {Object.values(ACCOUNTING_METHODOLOGY).map((methodology) => (
              <Option key={methodology} value={methodology}>
                {_.upperFirst(methodology)}
              </Option>
            ))}
          </Select>
        </Col>
        <Col sm={24} md={8}>
          <Select name="country" label="Country" rules={[{ required: true }]} initialValue={initial("country")}>
            {PROGRAM_COUNTRIES.map((country) => (
              <Option key={country} value={country}>
                {country}
              </Option>
            ))}
          </Select>
        </Col>
        <Col sm={24} md={8}>
          <Select name="currency" label="Currency" rules={[{ required: true }]} initialValue={initial("currency")}>
            {Object.values(CURRENCY_CODE).map((currency) => (
              <Option key={currency} value={currency}>
                {currency}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
      {switchField("noProgramTerms", "No T&C contract: farms join without accepting program terms")}
      {switchField("verificationRequired", "Verification required: farms provide documents before they can submit")}

      <AdminSection headline="Platform features" description="What farmers in this program see." />
      <Row gutter={16}>
        <Col sm={24} md={8}>
          <Flex vertical gap={8}>
            <Typography variant="paragraph-m" bold>
              Fields management
            </Typography>
            {switchField("platformFeatures.fieldManagement.enabled", "All fields view")}
            {switchField("platformFeatures.fieldManagement.wallet", "Wallet / payouts")}
            {switchField("platformFeatures.fieldManagement.outcomeBasedIncentives", "Outcome based incentives (inventory)")}
            {switchField("platformFeatures.fieldManagement.practiceBasedIncentives", "Practice based incentives (inventory)")}
            {switchField("platformFeatures.fieldManagement.impactPanel", "Impact panel")}
            {switchField("platformFeatures.fieldManagement.insightsImpactPanel", "Insights impact panel (SoilSight)")}
            {switchField("platformFeatures.fieldManagement.submission", "Submission")}
          </Flex>
        </Col>
        <Col sm={24} md={8}>
          <Flex vertical gap={8}>
            <Typography variant="paragraph-m" bold>
              Footprinting
            </Typography>
            {switchField("platformFeatures.footprinting.enabled", "Footprinting")}
            {switchField("platformFeatures.footprinting.results", "Results page")}
            {switchField("platformFeatures.footprinting.payouts", "Payout information")}
            {switchField("platformFeatures.footprinting.dairy", "Dairy")}
            {switchField("platformFeatures.footprinting.beef", "Beef")}
            {switchField("platformFeatures.footprinting.pork", "Pork")}
          </Flex>
        </Col>
        <Col sm={24} md={8}>
          <Flex vertical gap={8}>
            <Typography variant="paragraph-m" bold>
              Planning
            </Typography>
            {switchField("platformFeatures.planning.cropMixComposer", "Crop mix composer")}
          </Flex>
        </Col>
      </Row>

      <AdminSection headline="Fields management" description="Removals and inventory payouts." />
      <Row gutter={16}>
        <Col sm={24} md={8}>
          <Select
            name={path("fieldsManagement.methodology.socModel")}
            label="SOC model"
            rules={[{ required: true }]}
            initialValue={initial("fieldsManagement.methodology.socModel")}
          >
            <Option value="RegionalValues">Regional values</Option>
            <Option value="SoilPlus3">Soil+3</Option>
            <Option value="FieldSocModel">Field SOC model</Option>
          </Select>
        </Col>
        <Col sm={24} md={8}>
          <Input
            name={path("fieldsManagement.methodology.version")}
            label="SOC model version"
            rules={[{ required: true }, VERSION_RULE]}
            initialValue={initialText("fieldsManagement.methodology.version")}
          />
        </Col>
        <Col sm={24} md={8}>
          <InputNumber name={path("fieldsManagement.startYear")} label="Start year" initialValue={initialNumber("fieldsManagement.startYear")} />
        </Col>
        <Col sm={24} md={8}>
          <Select
            name={path("fieldsManagement.monitoringPeriodType")}
            label="Monitoring period"
            allowClear
            initialValue={initial("fieldsManagement.monitoringPeriodType")}
          >
            <Option value="calendar">Calendar year</Option>
            <Option value="harvest">Harvest year</Option>
          </Select>
        </Col>
        <Col sm={24} md={8}>
          <Input
            name={path("fieldsManagement.reportingPeriod.start")}
            label="Reporting period start (MM/DD)"
            rules={[MONTH_DAY_RULE]}
            initialValue={initialText("fieldsManagement.reportingPeriod.start")}
          />
        </Col>
        <Col sm={24} md={8}>
          <Input
            name={path("fieldsManagement.reportingPeriod.end")}
            label="Reporting period end (MM/DD)"
            rules={[MONTH_DAY_RULE]}
            initialValue={initialText("fieldsManagement.reportingPeriod.end")}
          />
        </Col>
        <Col sm={24} md={8}>
          <Input
            name={path("fieldsManagement.submissionDates.start")}
            label="Submission start (MM/DD)"
            rules={[MONTH_DAY_RULE]}
            initialValue={initialText("fieldsManagement.submissionDates.start")}
          />
        </Col>
        <Col sm={24} md={8}>
          <Input
            name={path("fieldsManagement.submissionDates.due")}
            label="Submission due (MM/DD)"
            rules={[MONTH_DAY_RULE]}
            initialValue={initialText("fieldsManagement.submissionDates.due")}
          />
        </Col>
        <Col sm={24} md={8}>
          <Input
            name={path("fieldsManagement.submissionDates.end")}
            label="Submission end (MM/DD)"
            rules={[MONTH_DAY_RULE]}
            initialValue={initialText("fieldsManagement.submissionDates.end")}
          />
        </Col>
        <Col sm={24}>
          <Typography variant="paragraph-s" color="grey-600" className="margin-bottom-100">
            Submission window: leave a date empty to use the platform default (see the Carbon Program Dates tab).
          </Typography>
        </Col>
        {isInventory && (
          <Col sm={24} md={8}>
            <Select
              name={path("fieldsManagement.fieldEnrollmentType")}
              label="Field enrollment"
              allowClear
              initialValue={initial("fieldsManagement.fieldEnrollmentType")}
            >
              <Option value={ENROLLMENT_TYPE.auto}>Automatic, all fields enrolled</Option>
              <Option value={ENROLLMENT_TYPE.user}>Farmer enrolls fields</Option>
            </Select>
          </Col>
        )}
        <Col sm={24} md={8}>
          <InputNumber
            name={path("fieldsManagement.payouts.removals.pointPrice")}
            label="Removals payout per point"
            step={0.001}
            initialValue={initialNumber("fieldsManagement.payouts.removals.pointPrice")}
          />
        </Col>
        <Col sm={24} md={8}>
          <InputNumber
            name={path("fieldsManagement.payouts.avoidances.pointPrice")}
            label="Avoidances payout per point (intervention)"
            step={0.001}
            initialValue={initialNumber("fieldsManagement.payouts.avoidances.pointPrice")}
          />
        </Col>
        <Col sm={24} md={8}>
          <InputNumber
            name={path("fieldsManagement.payouts.reductions.pointPrice")}
            label="Reductions payout per point (intervention)"
            step={0.001}
            initialValue={initialNumber("fieldsManagement.payouts.reductions.pointPrice")}
          />
        </Col>
        {isInventory && (
          <Col sm={24} md={8}>
            <InputNumber
              name={path("fieldsManagement.maximumPayoutPerHa")}
              label="Maximum payout per hectare"
              initialValue={initialNumber("fieldsManagement.maximumPayoutPerHa")}
            />
          </Col>
        )}
        <Col sm={24} md={12}>
          {cropSelect("fieldsManagement.programCrops", "Program crops (paid for)")}
        </Col>
        {isInventory && (
          <Col sm={24} md={12}>
            {cropSelect("fieldsManagement.projectArea.cropsOfInterest", "Crops of interest (project area)")}
          </Col>
        )}
      </Row>
      <Flex vertical gap={8} className="margin-bottom-200">
        <Typography variant="paragraph-m" bold>
          Practices and incentives
        </Typography>
        <Typography variant="paragraph-m" color="grey-600">
          {currentPractices.length ? `Currently: ${currentPractices.map((practice) => practice.slug).join(", ")}.` : "No practices yet."} To change them, copy
          the practices of a live program; describe anything else in the notes.
        </Typography>
        <Select name="practicesFromProgramId" label="Copy practices from" showSearch allowClear>
          {livePrograms.map((program) => (
            <Option key={program.id} value={program.id}>
              {getProgramInternalName(program)}
            </Option>
          ))}
        </Select>
      </Flex>

      <AdminSection headline="Footprinting" />
      <Row gutter={16}>
        <Col sm={24} md={8}>
          <InputNumber
            name={path("farmLevelFootprinting.startYear")}
            label="First footprinting year"
            rules={[{ required: true }]}
            initialValue={initialNumber("farmLevelFootprinting.startYear")}
          />
        </Col>
        <Col sm={24} md={8}>
          <Select
            name={path("farmLevelFootprinting.disabledYears")}
            label="Hidden years"
            mode="tags"
            maxTagCount="responsive"
            initialValue={initial("farmLevelFootprinting.disabledYears")}
          />
        </Col>
        <Col sm={24} md={8}>
          <InputNumber
            name={path("farmLevelFootprinting.verificationStartYear")}
            label="Verification start year"
            initialValue={initialNumber("farmLevelFootprinting.verificationStartYear")}
          />
        </Col>
        <Col sm={24} md={12}>
          {cropSelect("farmLevelFootprinting.programCrops", "Crops with footprinting forms")}
        </Col>
        <Col sm={24} md={12}>
          <Input
            name={path("farmLevelFootprinting.methodology.version")}
            label="Methodology version (practice management)"
            rules={[VERSION_RULE]}
            initialValue={initialText("farmLevelFootprinting.methodology.version")}
          />
        </Col>
        <Col sm={12} md={6}>
          {methodologySelect("farmLevelFootprinting.methodology.crops", "Crop methodology", CROPS_METHODOLOGY)}
        </Col>
        <Col sm={12} md={6}>
          {methodologySelect("farmLevelFootprinting.methodology.dairy", "Dairy methodology", DAIRY_METHODOLOGY)}
        </Col>
        <Col sm={12} md={6}>
          {methodologySelect("farmLevelFootprinting.methodology.beef", "Beef methodology", BEEF_METHODOLOGY)}
        </Col>
        <Col sm={12} md={6}>
          {methodologySelect("farmLevelFootprinting.methodology.pork", "Pork methodology", PORK_METHODOLOGY)}
        </Col>
      </Row>

      <Typography variant="paragraph-m" bold>
        Form completion payouts
      </Typography>
      <Row gutter={16}>
        <Col sm={24} md={8}>
          <Select
            name={path("farmLevelFootprinting.formSubmissionBonuses.cropPayouts")}
            label="Crop form payout"
            initialValue={initial("farmLevelFootprinting.formSubmissionBonuses.cropPayouts")}
          >
            <Option value="all-crops">Once all crop forms are complete</Option>
            <Option value="per-crop">For each crop form</Option>
          </Select>
        </Col>
      </Row>
      <Form.List name="formBonusRows" initialValue={initialValues.formBonusRows}>
        {(rows, { add, remove }) => (
          <>
            {rows.map((row) => (
              <Row gutter={8} key={row.key} align="middle">
                <Col sm={4}>
                  <InputNumber
                    name={[row.name, "year"]}
                    label="Year"
                    rules={[{ required: true }]}
                    initialValue={initialValues.formBonusRows?.[row.name]?.year}
                  />
                </Col>
                {(["farm", "crop", "dairy", "beef"] as const).map((form) => (
                  <Col sm={4} key={form}>
                    <InputNumber name={[row.name, form]} label={_.upperFirst(form)} initialValue={initialValues.formBonusRows?.[row.name]?.[form]} />
                  </Col>
                ))}
                <Col sm={4}>
                  <Button type="text" onClick={() => remove(row.name)}>
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}
            <Button type="secondary" onClick={() => add({})} className="margin-bottom-200">
              Add payout year
            </Button>
          </>
        )}
      </Form.List>

      <AdminSection headline="Notes" description="Anything the form can't express: bespoke incentives, deadlines, context for the reviewer." />
      <TextArea name="notes" label="Notes" rows={4} initialValue={initialNotes} />
    </Form>
  );
};
